import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import './Chat.css';

import { notifyUser } from "../../utils/notifyUser";

const socket = io(process.env.REACT_APP_API_SOCKET_URL);

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
// const BASE_UPLOAD_PATH = "/uploads/chat_documents/";

// Get initials from username
const getInitials = (name) => {
    if(!name) return '?';
    return name
        .split(' ')
        .map((part) => part[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

// Format timestamp
const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
    });
};

// Format date for message groups
const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
};

// EmptyState component to display when no chat is selected
const EmptyState = () => {
    return (
        <div className='empty-state'>
            <div className='icon-circle'>
                <i className='fas fa-comments'></i>
            </div>
            <h2>WELCOME TO AQI</h2>
            <p className='empty-state-description'>
                Select a conversation or start a new chat to begin messaging
            </p>

            <div className='feature-cards'>
                <div className='feature-card'>
                    <i className='fas fa-users feature-icon'></i>
                    <div className='feature-content'>
                        <h3>Connect with others</h3>
                        <p>Search for users to start a conversation</p>
                    </div>
                </div>

                <div className='feature-card'>
                    <i className='fas fa-bolt feature-icon'></i>
                    <div className='feature-content'>
                        <h3>Real-time messaging</h3>
                        <p>Enjoy instant communication with your contacts</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

function Chat({
    user,
    otherUser = null,
    contextType = 'global',
    contextId = null,
    investors = [],
    // unreadMessageCount = 0,
    // setUnreadMessageCount = null,
    fetchUnreadCount = null,
}) {
    const navigate = useNavigate();
    // const [user, setUser] = useState(null)
    const [searchUser, setSearchUser] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [chatUsers, setChatUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState('');
    const [isMobileView, setIsMobileView] = useState(false);
    const [showUserList, setShowUserList] = useState(true);
    const messagesEndRef = useRef(null);
    const previousSelectedUserRef = useRef(null);
    const isBroadcast = otherUser?.id === 'all';

    // Check if device is mobile
    useEffect(() => {
        const checkMobileView = () => {
            setIsMobileView(window.innerWidth <= 768);
            if(window.innerWidth > 768) {
                setShowUserList(true);
            }
        };

        checkMobileView();
        window.addEventListener('resize', checkMobileView);
        return () => window.removeEventListener('resize', checkMobileView);
    }, []);

    // Auto-hide user list on mobile when chat is selected
    useEffect(() => {
        if(isMobileView && selectedUser) {
            setShowUserList(false);
        }
    }, [selectedUser, isMobileView]);

    // Fetch logged-in user from jwt token
    useEffect(() => {
        if(user) {
            socket.emit('login', user.id);

            fetchChatHistory(user.id);
        }

        // Cleanup function to handle component unmount or user change
        return () => {
            if(user?.id) {
                socket.emit('leave_chat', { senderId: user.id });
            }
        };
    }, [user]);

    // Auto-open chat when an investor (otherUser) is passed in
    useEffect(() => {
        if(!user || !otherUser) return;

        if(isBroadcast) {
            // open the chat box in broadcast mode
            // triggers the chat box to render
            setSelectedUser('all');
            // start with an empty thread
            setMessages([]);
            return;
        }

        // existing 1-to-1 logic
        setChatUsers((prev) => {
            const exists = prev.some((u) => u.id === otherUser.id);
            return exists ? prev : [otherUser, ...prev];
        });

        loadMessages(otherUser.id);
    }, [otherUser, user]);

    // Fetch users who have previous chat history
    const fetchChatHistory = async (userId) => {
        let users = [];
        try {
            const res = await axios.get(`${BASE_URL}/api/users/chat-history/${userId}`, {
                withCredentials: true,
            });
            if(res.data && Array.isArray(res.data)) {
                users = res.data;
            }
            // store unread message count for each user
            setChatUsers(
                users.map((user) => ({
                    ...user,
                    // store unread message count
                    unreadCount: user.unread_count,
                }))
            );
        } catch(err) {
            console.error('Error fetching chat history:', err);
            setChatUsers([]);
        }
    };

    // ensure All-Investors chat pane appears
    useEffect(() => {
        if(!otherUser || otherUser.id !== 'all') return;
        setSelectedUser('all');
        setMessages([]);
    }, [otherUser]);

    // Handle selectedUser change - emit leave_chat when user changes or leaves chat
    useEffect(() => {
        const previousUser = previousSelectedUserRef.current;

        // Update the ref with current selectedUser
        previousSelectedUserRef.current = selectedUser;

        // If we're changing from one valid chat to another or to null, emit leave_chat for the previous
        if(user?.id && previousUser && previousUser !== 'all' && previousUser !== selectedUser) {
            socket.emit('leave_chat', { senderId: user.id });
            // Clear the global chat user indicator when changing chats
            window.currentChatUserId = null;
        }

        // Set current chat user for toast prevention
        if(selectedUser && selectedUser !== 'all') {
            window.currentChatUserId = selectedUser;
        } else {
            window.currentChatUserId = null;
        }
    }, [selectedUser, user?.id]);

    // Component-level cleanup for when user completely leaves the chat component
    useEffect(() => {
        return () => {
            // This runs when the entire Chat component unmounts
            // Always emit leave_chat on unmount to ensure user is removed from activeChats
            if(user?.id) {
                socket.emit('leave_chat', { senderId: user.id });
            }
            // Clear global chat user indicator
            window.currentChatUserId = null;
        };
    }, []);

    // Search for users
    const searchUsers = async () => {
        let results = [];
        if(!searchUser.trim()) {
            setSearchResults([]);
            return;
        }

        try {
            const res = await axios.get(`${BASE_URL}/api/users/chat-search?search=${searchUser}`, {
                withCredentials: true,
            });
            if(res.data && Array.isArray(res.data)) {
                results = res.data;
            }
            setSearchResults(results);
        } catch(error) {
            console.error('Error searching users:', error);
            setSearchResults([]);
        }
    };

    // Fetch chat messages between the logged-in user and the selected user
    const loadMessages = async (receiverId) => {
        let messages = [];
        try {
            const res = await axios.get(
                `${BASE_URL}/api/messages?senderId=${user.id}&receiverId=${receiverId}`,
                { withCredentials: true }
            );
            if(res.data && Array.isArray(res.data)) {
                messages = res.data;
            }
            setMessages(messages);
            setSelectedUser(receiverId);

            // On mobile, hide user list when chat is opened
            if(isMobileView) {
                setShowUserList(false);
            }

            // Check if this user is already in chatUsers
            const userExists = chatUsers.some((u) => u.id === receiverId);
            // If user is from search results and not in chatUsers, add them
            if(!userExists && searchResults.length > 0) {
                const foundUser = searchResults.find((u) => u.id === receiverId);
                if(foundUser) {
                    setChatUsers((prevUsers) => [
                        ...prevUsers,
                        {
                            id: foundUser.id,
                            username:
                                foundUser.first_name && foundUser.last_name
                                    ? `${foundUser.first_name} ${foundUser.last_name}`
                                    : foundUser.username,
                            last_message: null,
                            last_message_time: null,
                            unreadCount: 0,
                        },
                    ]);
                }
            }

            // Emit event to server that the user is viewing the chat
            socket.emit('view_chat', { senderId: user.id, receiverId });

            // Set global variable to indicate current chat user (for App.js toast logic)
            window.currentChatUserId = receiverId;

            // Reset unread count to 0 for the selected user
            setChatUsers((prevUsers) =>
                prevUsers.map((u) => (u.id === receiverId ? { ...u, unreadCount: 0 } : u)));

            // Mark all messages as seen once the chat is opened
            messages.forEach((msg) => {
                if(!msg.is_seen) {
                    socket.emit('view_message', {
                        messageId: msg.id,
                        userId: user.id,
                    });
                }
            });

            // Update global unread count after opening a chat
            if(fetchUnreadCount) {
                await fetchUnreadCount();
            }

            // Clear search results only if user was selected from search, not chat history
            if(searchResults.length > 0) {
                // Hide search results after selecting a user
                setSearchResults([]);
                // clear search input
                setSearchUser('');
            }
        } catch(err) {
            console.error('Error loading messages:', err);
            setMessages([]);
        }
    };

    // Handle sending messages
    const sendMessage = async () => {
        if(!message.trim() || !selectedUser) return;

        const timestamp = new Date().toISOString();

        if(selectedUser === 'all') {
            // Get all investor IDs except current user and "all"
            const receivers = investors.filter((inv) => inv.id !== user.id).map((inv) => inv.id);

            try {
                await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/broadcast`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        senderId: user.id,
                        receivers,
                        contextType,
                        contextId,
                        message,
                        timestamp,
                    }),
                });

                // Optionally show "sent to all" confirmation in chat UI
                setMessages((prevMessages) => [
                    ...prevMessages,
                    {
                        sender_id: user.id,
                        message: `[Broadcast] ${message}`,
                        timestamp,
                    },
                ]);
            } catch(err) {
                console.error('Broadcast message failed:', err);
            }
        } else {
            // Normal 1-to-1 message
            const newMessage = {
                senderId: user.id,
                receiverId: selectedUser,
                message,
                timestamp,
            };

            socket.emit('send_message', newMessage);

            setMessages((prevMessages) => [
                ...prevMessages,
                { sender_id: user.id, message, timestamp },
            ]);
        }

        setMessage('');
    };

    // Handle Receiving messages
    useEffect(() => {
        const handleReceiverMessage = async (newMessage) => {
            // Ensure timestamp is present
            const timestamp = newMessage.timestamp ? new Date(newMessage.timestamp) : new Date();

            // Only process received messages that are from other users, not messages we sent ourselves
            if(
                newMessage.senderId !== user.id &&
                selectedUser &&
                newMessage.senderId === selectedUser
            ) {
                // If the selected user sent a message, update the chat instantly
                setMessages((prevMessages) => [
                    ...prevMessages,
                    {
                        sender_id: newMessage.senderId,
                        message: newMessage.message,
                        timestamp: timestamp.toLocaleString(),
                    },
                ]);
            } else if(newMessage.senderId !== user.id) {
                // If the user is NOT in the chat with the sender, fetch the latest unread count from the backend
                let users = [];
                try {
                    const res = await axios.get(`${BASE_URL}/api/users/chat-history/${user.id}`, {
                        withCredentials: true,
                    });
                    if(res.data && Array.isArray(res.data)) {
                        users = res.data;
                    }
                    setChatUsers(
                        users.map((user) => ({
                            ...user,
                            // Update unread count from backend
                            unreadCount: user.unread_count,
                        }))
                    );
                } catch{
                    console.error('Error fetching chat history');
                    setChatUsers([]);
                }

                // Update global unread count
                if(fetchUnreadCount) {
                    fetchUnreadCount();
                }
            }
        };

        // Listen for new messages
        socket.on('receive_message', handleReceiverMessage);

        return () => {
            socket.off('receive_message', handleReceiverMessage);
        };
    }, [selectedUser, user?.id]);

    // Show a toast notification when the receiver is offline
    useEffect(() => {
        socket.on('user_offline', () => {
            notifyUser(null, "warning", `User is offline. They will receive the message when they come online`, "toast", null, null, 5000, null, null, false, false, "top-center");
        });

        return () => {
            socket.off('user_offline');
        };
    }, []);

    // Force logout the user
    useEffect(() => {
        socket.on('force_logout', (data) => {
            // Clear user session
            localStorage.removeItem('userToken');
            // Show logout message
            notifyUser(null, "error", data.message || 'You have been logged out', "toast", null, null, null, null, null, false, false, "top-center");
            // Redirect to login
            navigate('/login');
        });

        return () => {
            socket.off('force_logout');
        };
    }, [navigate]);

    // Format the Message Time
    const formatMessageTime = (timestamp) => {
        const now = new Date();
        const messageDate = new Date(timestamp);

        if(
            messageDate.getDate() === now.getDate() &&
            messageDate.getMonth() === now.getMonth() &&
            messageDate.getFullYear() === now.getFullYear()
        ) {
            return formatTime(timestamp);
        } else if(messageDate.getFullYear() === now.getFullYear()) {
            // Same year but different day
            return messageDate.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
            });
        } else {
            // Different year
            return messageDate.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
        }
    };

    // Scroll to bottom of messages
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
        });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const fetchOnlineStatus = async () => {
        try {
            let onlineStatusMap = {};
            const res = await axios.get(`${BASE_URL}/api/users/online-status`, {
                withCredentials: true,
            });
            // Map user_id to online status
            if(res.data && Array.isArray(res.data) && res.data.length > 0) {
                onlineStatusMap = res.data.reduce((acc, user) => {
                    if(user && user.user_id !== undefined) {
                        acc[user.user_id] = user.status === 'online';
                    }
                    return acc;
                }, {});
            } else {
                console.warn('Online status data is not in expected format:', res.data);
            }
            setChatUsers((prevUsers) =>
                prevUsers.map((user) => ({
                    ...user,
                    isOnline: onlineStatusMap[user.id] || false,
                })));
        } catch(err) {
            console.error('Error fetching online status:', err);
        }
    };

    useEffect(() => {
        // Fetch online status on component mount
        fetchOnlineStatus();

        const interval = setInterval(() => {
            // Fetch online status every 10 seconds
            fetchOnlineStatus();
        }, 10000);
        // Cleanup interval on component unmount
        return () => clearInterval(interval);
    }, []);

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if(!file || !selectedUser) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('senderId', user.id);
        formData.append('receiverId', selectedUser);

        try {
            const response = await axios.post(`${BASE_URL}/messages/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                withCredentials: true,
            });

            const fileMessage = {
                sender_id: user.id,
                message: 'File uploaded',
                file_path: response.data.filePath,
                timestamp: new Date().toISOString(),
            };

            setMessages((prevMessages) => [...prevMessages, fileMessage]);
            notifyUser(null, "success", 'File uploaded successfully!', "toast", null, null, null, null, null, false, false, "top-center");
        } catch(error) {
            console.error('Error uploading file:', error);
            notifyUser(null, "error", 'Failed to upload file. Please try again.', "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    return (
        <div className='chat-container'>
            <div className='app-header'>
                <h1>
                    <i className='fas fa-comment-dots'></i> AQI
                </h1>
                {/* {user && (
          <div className="user-status">
            <span className="status-indicator"></span>
            <span>Logged in as {user.username}</span>
          </div>
        )} */}
            </div>

            {/* Main layout */}
            <div className='main-container'>
                {/* User List - conditionally shown on mobile */}
                <div
                    className={`user-list ${isMobileView && !showUserList ? 'mobile-hidden' : ''}`}
                >
                    {/* <div className="user-list-header">
            <h2>Conversations</h2>
          </div> */}

                    {/* Search Bar */}
                    <div className='search-bar'>
                        <input
                            type='text'
                            placeholder='Search for users'
                            value={searchUser}
                            onChange={(e) => setSearchUser(e.target.value)}
                            onKeyDown={(e) => {
                                if(e.key === 'Enter') {
                                    searchUsers();
                                }
                            }}
                        />
                        <i className='fas fa-search search-icon' onClick={searchUsers}></i>
                    </div>

                    {/* User list of previous chats */}
                    <div className='user-list-content'>
                        {/* Display Chat History */}
                        {searchResults.length === 0 && (
                            <>
                                {chatUsers.length === 0 ? (
                                    <p>No conversations yet</p>
                                ) : (
                                    chatUsers.map((u) => (
                                        <div
                                            key={u.id}
                                            className={`user-item ${
                                                selectedUser === u.id ? 'active' : ''
                                            }`}
                                            onClick={() => loadMessages(u.id)}
                                        >
                                            {' '}
                                            <div className='user-item-info'>
                                                <div className='user-avatar'>
                                                    {getInitials(
                                                        u.first_name && u.last_name
                                                            ? `${u.first_name} ${u.last_name}`
                                                            : u.username
                                                    )}
                                                    <span
                                                        className={`status-indicator ${
                                                            u.isOnline ? 'online' : 'offline'
                                                        }`}
                                                        title={u.isOnline ? 'Online' : 'Offline'}
                                                    ></span>
                                                </div>
                                                <div className='user-details'>
                                                    <span className='user-name'>
                                                        {u.first_name && u.last_name
                                                            ? `${u.first_name} ${u.last_name}`
                                                            : u.username}
                                                        {/* {u.isOnline && <span className="online-indicator" title="Online"></span>} */}
                                                    </span>
                                                    <span className='user-last-message'>
                                                        {u.last_message
                                                            ? u.last_message.length > 20
                                                                ? u.last_message.substring(0, 20) +
                                                                  '...'
                                                                : u.last_message
                                                            : 'Start a conversation'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className='user-meta'>
                                                {u.last_message_time && (
                                                    <span className='user-time'>
                                                        {formatMessageTime(u.last_message_time)}
                                                    </span>
                                                )}
                                                {u.unreadCount > 0 && (
                                                    <span className='badge'>{u.unreadCount}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </>
                        )}{' '}
                        {/* Display Search Results */}
                        {searchResults.length > 0 && (
                            <div className='search-results'>
                                {searchResults.map((u) => (
                                    <div
                                        key={u.id}
                                        className='user-item'
                                        onClick={() => loadMessages(u.id)}
                                    >
                                        <div className='user-item-info'>
                                            <div className='user-avatar'>
                                                {getInitials(
                                                    u.first_name && u.last_name
                                                        ? `${u.first_name} ${u.last_name}`
                                                        : u.username
                                                )}
                                            </div>
                                            <div className='user-details'>
                                                <span className='user-name'>
                                                    {u.first_name && u.last_name
                                                        ? `${u.first_name} ${u.last_name}`
                                                        : u.username}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Chat Box or Empty State - conditionally shown on mobile */}
                {selectedUser ? (
                    <div
                        className={`chat-box ${
                            isMobileView && showUserList ? 'mobile-hidden' : ''
                        }`}
                    >
                        <div className='chat-header'>
                            {/* Mobile back button in chat header */}
                            {isMobileView && (
                                <button
                                    className='mobile-chat-back-button'
                                    onClick={() => {
                                        setShowUserList(true);
                                        if(window.innerWidth <= 768) {
                                            setSelectedUser(null);
                                        }
                                    }}
                                    title='Back to conversations'
                                >
                                    <i className='fas fa-arrow-left'></i>
                                </button>
                            )}
                            <div className='chat-header-info'>
                                {' '}
                                <div className='chat-header-avatar'>
                                    {selectedUser === 'all'
                                        ? 'AI'
                                        : getInitials(
                                              chatUsers.find((u) => u.id === selectedUser)
                                                  ?.first_name &&
                                                  chatUsers.find((u) => u.id === selectedUser)
                                                      ?.last_name
                                                  ? `${
                                                        chatUsers.find((u) => u.id === selectedUser)
                                                            ?.first_name
                                                    } ${
                                                        chatUsers.find((u) => u.id === selectedUser)
                                                            ?.last_name
                                                    }`
                                                  : chatUsers.find((u) => u.id === selectedUser)
                                                        ?.username
                                          )}
                                </div>
                                <div className='chat-header-details'>
                                    <span className='chat-header-name'>
                                        {selectedUser === 'all'
                                            ? 'All Investors'
                                            : chatUsers.find((u) => u.id === selectedUser)
                                                    ?.first_name &&
                                                chatUsers.find((u) => u.id === selectedUser)
                                                    ?.last_name
                                              ? `${
                                                    chatUsers.find((u) => u.id === selectedUser)
                                                        ?.first_name
                                                } ${
                                                    chatUsers.find((u) => u.id === selectedUser)
                                                        ?.last_name
                                                }`
                                              : chatUsers.find((u) => u.id === selectedUser)
                                                    ?.username || '...'}
                                    </span>
                                    <span className='chat-header-status'>
                                        {selectedUser === 'all'
                                            ? 'Broadcast message'
                                            : chatUsers.find((u) => u.id === selectedUser)?.isOnline
                                              ? 'Online'
                                              : 'Offline'}
                                    </span>
                                </div>
                            </div>

                            {/* <div className="chat-header-actions">
                <button title="Call">
                  <i className="fas fa-phone"></i>
                </button>
                <button title="Video">
                  <i className="fas fa-video"></i>
                </button>
                <button title="Info">
                  <i className="fas fa-info-circle"></i>
                </button>
              </div> */}
                        </div>
                        <div className='messages'>
                            {messages.length === 0 ? (
                                <p className='empty-chat-message'>
                                    No messages yet. Start the conversation!
                                </p>
                            ) : (
                                messages.reduce((acc, msg, index) => {
                                    // Format the timestamp to display only hour and minutes
                                    // const timestamp = new Date(msg.timestamp); // removed unused variable
                                    const formattedTime = formatTime(msg.timestamp);
                                    // Format the date for comparison
                                    const currentMessageDate = formatDate(msg.timestamp);
                                    const previousMessageDate =
                                        index > 0
                                            ? formatDate(messages[index - 1].timestamp)
                                            : null;

                                    // Add a date separator if the current message is from a different day
                                    if(currentMessageDate !== previousMessageDate) {
                                        acc.push(
                                            <div key={`date-${index}`} className='date-separator'>
                                                {currentMessageDate}
                                            </div>
                                        );
                                    }

                                    const isFileMessage =
                                        msg.file_path !== null && msg.file_path !== undefined;

                                    acc.push(
                                        <p
                                            key={index}
                                            className={
                                                msg.sender_id === user.id ? 'sent' : 'received'
                                            }
                                        >
                                            {isFileMessage ? (
                                                <a
                                                    href={
                                                        msg.file_path.startsWith('http')
                                                            ? msg.file_path
                                                            : `${BASE_URL}${msg.file_path}`
                                                    }
                                                    // Ensure no double prefix
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                    className='file-link'
                                                >
                                                    {msg.file_path.split('/').pop() || 'File'}{' '}
                                                    {/* Safely handle file_path */}
                                                </a>
                                            ) : (
                                                msg.message || 'Message not available'
                                            )}
                                            <span className='timestamp'>{formattedTime}</span>

                                            {/* Only show tick marks for sent messages (not for received messages) */}
                                            {msg.sender_id === user.id && (
                                                <span
                                                    className={`message-status ${
                                                        msg.is_seen ? 'seen' : 'delivered'
                                                    }`}
                                                >
                                                    {msg.is_seen ? (
                                                        <svg
                                                            width='16'
                                                            height='16'
                                                            fill='#4fc3f7'
                                                            viewBox='0 0 24 24'
                                                        >
                                                            <path d='M1 13l5 5L20 4l2 2L6 20 0 14z' />
                                                        </svg>
                                                    ) : (
                                                        <svg
                                                            width='16'
                                                            height='16'
                                                            fill='#888'
                                                            viewBox='0 0 24 24'
                                                        >
                                                            <path d='M1 13l5 5L17 7l2 2L6 20 0 14z' />
                                                        </svg>
                                                    )}
                                                </span>
                                            )}
                                        </p>
                                    );

                                    return acc;
                                }, [])
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                        <div className='message-input'>
                            <div className='message-input-actions'>
                                {/* <button
                  title="Attach"
                  onClick={() => document.getElementById("fileInput").click()}
                >
                  <i className="fas fa-paperclip"></i>
                </button> */}
                                <input
                                    type='file'
                                    id='fileInput'
                                    style={{ display: 'none' }}
                                    onChange={handleFileUpload}
                                />
                            </div>
                            <input
                                type='text'
                                placeholder='Type a message'
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                onKeyDown={(e) => {
                                    if(e.key === 'Enter') {
                                        sendMessage();
                                    }
                                }}
                            />
                            <button className='send-button' onClick={sendMessage}>
                                Send <i className='fas fa-paper-plane'></i>
                            </button>
                        </div>
                    </div>
                ) : (
                    <div
                        className={`empty-chat-container ${
                            isMobileView && showUserList ? 'mobile-hidden' : ''
                        }`}
                    >
                        <EmptyState />
                    </div>
                )}
            </div>
        </div>
    );
}

Chat.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        status: PropTypes.string,
        unread_count: PropTypes.number,
    }).isRequired,
    otherUser: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        username: PropTypes.string,
    }),
    contextType: PropTypes.string,
    contextId: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
    investors: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            first_name: PropTypes.string,
            last_name: PropTypes.string,
            username: PropTypes.string,
        })
    ),
    fetchUnreadCount: PropTypes.func,
};

export default Chat;
